import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, TriangleAlert, Video, X } from 'lucide-react';
import { ALBUM_MAX_PHOTOS, ALBUM_VIDEO_MIME_TYPES, CONSENT_COPY, DAILY_LIFE_COPY, isVideoMime } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, ErrorNote, Field, Input, Spinner, Textarea } from '@/components/ui';
import { api, apiData } from '@/lib/api';
import { todayISO } from '@/lib/format';
import { uploadMedia } from '@/lib/upload';
import type { ConsentCheck, TaggableChild } from '@/types';

interface DraftPhoto {
  mediaId: string;
  previewUrl: string;
  caption: string;
  childIds: string[];
  /** 영상이면 <video>로 미리 본다 (§5.5 v1.22) */
  isVideo: boolean;
}

/** 앨범 작성 — 명세서 §5.5 */
export function AlbumEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [activityDate, setActivityDate] = useState(todayISO());
  const [photos, setPhotos] = useState<DraftPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: children, isPending } = useQuery({
    queryKey: ['albums', 'taggable-children'],
    queryFn: () => apiData<TaggableChild[]>('/albums/taggable-children'),
  });

  const taggedChildIds = [...new Set(photos.flatMap((photo) => photo.childIds))];

  // 태그가 바뀔 때마다 초상권 미동의 원아를 확인한다 (§5.5).
  const { data: consent } = useQuery({
    // 순서만 다른 같은 조합이 같은 캐시를 쓰도록 정렬해 정규화한다
    queryKey: ['albums', 'consent', [...taggedChildIds].sort().join(',')],
    queryFn: () =>
      apiData<ConsentCheck>('/albums/check-consent', {
        method: 'POST',
        body: { childIds: taggedChildIds },
      }),
    enabled: taggedChildIds.length > 0,
  });

  const save = useMutation({
    mutationFn: (status: 'DRAFT' | 'PUBLISHED') =>
      api('/albums', {
        method: 'POST',
        body: {
          title,
          description: description || undefined,
          activityDate,
          status,
          photos: photos.map((photo) => ({
            mediaId: photo.mediaId,
            caption: photo.caption || undefined,
            childIds: photo.childIds,
          })),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['albums'] });
      navigate('/album');
    },
  });

  /** presigned URL 발급 → 직접 업로드 → mediaId 보관 (§11) */
  const handleFiles = async (files: FileList) => {
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, ALBUM_MAX_PHOTOS - photos.length)) {
        const isVideo = isVideoMime(file.type);
        setUploadingVideo(isVideo);
        const mediaId = await uploadMedia(file);

        setPhotos((prev) => [
          ...prev,
          {
            mediaId,
            previewUrl: URL.createObjectURL(file),
            caption: '',
            childIds: [],
            isVideo,
          },
        ]);
      }
    } catch (error) {
      setUploadError((error as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const toggleTag = (index: number, childId: string) => {
    setPhotos((prev) =>
      prev.map((photo, i) =>
        i === index
          ? {
              ...photo,
              childIds: photo.childIds.includes(childId)
                ? photo.childIds.filter((id) => id !== childId)
                : [...photo.childIds, childId],
            }
          : photo,
      ),
    );
  };

  const canSave = title.trim().length > 0 && photos.length > 0;

  return (
    <Screen withNav={false}>
      <AppBar title="새 앨범" back />

      <div className="flex flex-col gap-4 p-4">
        <Card>
          <Field label="제목" required>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 봄나들이"
            />
          </Field>
          <Field label="날짜">
            <Input
              type="date"
              value={activityDate}
              onChange={(e) => setActivityDate(e.target.value)}
            />
          </Field>
          <Field label="설명">
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="어떤 활동이었는지 적어 주세요."
            />
          </Field>
        </Card>

        {consent?.warning && (
          <Card className="bg-secondary-soft">
            <p className="flex items-start gap-1.5 text-sm">
              <TriangleAlert size={15} className="mt-0.5 shrink-0" />
              <span>{consent.warning}</span>
            </p>
            <p className="mt-1.5 text-sm text-ink-sub">{CONSENT_COPY.photoBody}</p>
          </Card>
        )}

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <p className="font-semibold">
              사진·영상 <span className="tnum text-ink-sub">{photos.length}/{ALBUM_MAX_PHOTOS}</span>
            </p>
            <label className="tap inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-line-strong px-3 py-2 text-sm font-medium">
              <ImagePlus size={16} />
              사진·영상 고르기
              <input
                type="file"
                accept={`image/*,${ALBUM_VIDEO_MIME_TYPES.join(',')}`}
                multiple
                className="hidden"
                onChange={(e) => e.target.files && void handleFiles(e.target.files)}
              />
            </label>
          </div>

          {uploading && (
            <Spinner label={uploadingVideo ? DAILY_LIFE_COPY.album.videoUploading : '사진을 올리는 중이에요'} />
          )}
          {uploadError && <ErrorNote message={uploadError} />}
          {isPending && <Spinner />}

          <ul className="flex flex-col gap-4">
            {photos.map((photo, index) => (
              <li key={photo.mediaId} className="border-t border-line pt-3 first:border-0 first:pt-0">
                <div className="flex gap-3">
                  {photo.isVideo ? (
                    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-sm bg-primary-soft">
                      <video src={photo.previewUrl} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                      <span className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 rounded-full bg-surface/85 px-1.5 py-0.5 text-[11px] font-semibold text-ink">
                        <Video size={11} /> {DAILY_LIFE_COPY.album.videoBadge}
                      </span>
                    </div>
                  ) : (
                    <img
                      src={photo.previewUrl}
                      alt=""
                      className="h-20 w-20 shrink-0 rounded-sm object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <Input
                      value={photo.caption}
                      placeholder="사진 설명 (선택)"
                      onChange={(e) =>
                        setPhotos((prev) =>
                          prev.map((p, i) => (i === index ? { ...p, caption: e.target.value } : p)),
                        )
                      }
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="사진 빼기"
                    onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <X size={16} />
                  </Button>
                </div>

                <p className="mb-1.5 mt-2 text-sm text-ink-sub">
                  이 사진에 담긴 아이 — 태그된 아이의 가정에만 보여요
                </p>
                <div className="flex flex-wrap gap-2">
                  {children?.map((child) => (
                    <Chip
                      key={child.id}
                      tone={photo.childIds.includes(child.id) ? 'primary' : 'neutral'}
                      selected={photo.childIds.includes(child.id)}
                      onClick={() => toggleTag(index, child.id)}
                    >
                      {child.name}
                      {!child.photoConsent && <TriangleAlert size={12} />}
                    </Chip>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {save.isError && <ErrorNote message={(save.error as Error).message} />}

        <div className="flex gap-2">
          <Button
            variant="outline"
            block
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate('DRAFT')}
          >
            임시저장
          </Button>
          <Button
            block
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate('PUBLISHED')}
          >
            발행
          </Button>
        </div>
      </div>
    </Screen>
  );
}
